import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Composant de navigation bandeau d'en-tête.
 * Affiche par défaut DEUX boutons côte à côte : "Retour" et "Accueil".
 *
 * Rétro-compatibilité :
 *   - Si appelé avec to="/" et label="Accueil" (ancien pattern), on rend un seul
 *     bouton Accueil pour ne pas casser les usages existants.
 *   - Sinon, on rend "Retour" + "Accueil".
 *
 * @param {string} to - Si défini, clic Retour navigue vers cette route (sinon navigate(-1))
 * @param {string} label - Texte du bouton principal (défaut "Retour")
 * @param {string} className - Classes CSS additionnelles
 * @param {string} variant - "default" (fond blanc) ou "dark" (fonds sombres)
 * @param {string} fallback - Route de fallback si pas d'historique
 * @param {boolean} showHome - Affiche le bouton Accueil (défaut true)
 * @param {string|object} homeRoute - V38.32 — Override de la destination du bouton "Accueil".
 *   Soit une route string (ex: "/admin/ccmi-dashboard"), soit un objet
 *   { super_admin: "/x", admin: "/y", ... } pour route différenciée par rôle.
 * @param {string} homeLabel - V38.32 — Texte affiché sur le bouton Accueil (défaut "Accueil")
 */
const BackButton = ({ to, label = "Retour", className = "", variant = "default", fallback = null, showHome = true, homeRoute = null, homeLabel = "Accueil" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Mode rétro-compat : l'ancien <BackButton to="/" label="Accueil" /> = un seul bouton
  const isLegacyHomeOnly = to === "/" && label === "Accueil";

  const getDashboardRoute = () => {
    // V38.32 — Override prioritaire si `homeRoute` fourni
    if (typeof homeRoute === 'string' && homeRoute) {
      return homeRoute;
    }
    if (homeRoute && typeof homeRoute === 'object') {
      const role = user?.role;
      if (role && homeRoute[role]) return homeRoute[role];
      if (homeRoute._default) return homeRoute._default;
    }
    const dashboardRoutes = {
      super_admin: '/admin/super-dashboard',
      admin: '/admin/dashboard',
      cooper: '/cooper/dashboard',
      member: '/member/dashboard',
      expert: '/expert/dashboard',
    };
    return dashboardRoutes[user?.role] || '/';
  };

  const getFallbackRoute = () => {
    if (fallback) return fallback;
    return getDashboardRoute();
  };

  const handleBack = () => {
    // V35.65 — Retour réellement sûr :
    // 1. Si `to` explicite → navigate(to)
    // 2. Sinon, détecter si un VRAI historique applicatif existe en lisant
    //    `window.history.state.idx` (géré par react-router v6). idx === 0
    //    signifie qu'on est arrivé directement sur cette page (refresh,
    //    lien email, retour Stripe) → `navigate(-1)` ferait sortir de
    //    l'app et reviendrait à la page de connexion. Dans ce cas on
    //    tombe sur le fallback (dashboard du rôle).
    if (to) {
      navigate(to);
      return;
    }
    const historyIdx = window.history.state?.idx;
    if (typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1);
    } else {
      navigate(getFallbackRoute());
    }
  };

  // V35.45 — Le bouton "Accueil" ramène vers le DASHBOARD de l'espace connecté
  // (et non vers la racine "/" qui mène à la page de connexion).
  const handleHome = () => navigate(getDashboardRoute());

  const isDarkBackground = className.includes('text-white') || variant === 'dark';

  const baseStyles = isDarkBackground
    ? 'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/20 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md'
    : 'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md';

  const homeStyles = isDarkBackground
    ? 'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#0a3d62] bg-[#DEA900] hover:bg-[#f5cc3e] border border-[#DEA900] rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#DEA900] transition-all duration-200 shadow-sm hover:shadow-md'
    : 'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#0a3d62] bg-[#DEA900] hover:bg-[#f5cc3e] border border-[#DEA900] rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#DEA900] transition-all duration-200 shadow-sm hover:shadow-md';

  const cleanClassName = className.replace(/text-white/g, '').replace(/mb-\d+/g, '').trim();
  const marginClass = className.match(/mb-\d+/)?.[0] || '';

  // Mode rétro-compatible : un seul bouton Accueil
  if (isLegacyHomeOnly) {
    return (
      <button
        onClick={handleHome}
        className={`${baseStyles} ${marginClass} ${cleanClassName}`}
        data-testid="back-button-home-legacy"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Accueil</span>
      </button>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${marginClass}`} data-testid="back-button-group">
      <button
        onClick={handleBack}
        className={`${baseStyles} ${cleanClassName}`}
        data-testid="back-button-back"
        title="Retour à la page précédente"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>{label}</span>
      </button>
      {showHome && (
        <button
          onClick={handleHome}
          className={homeStyles}
          data-testid="back-button-home"
          title={homeLabel === "Accueil" ? "Retour à la page d'accueil" : `Aller vers ${homeLabel}`}
        >
          <Home className="w-4 h-4" />
          <span>{homeLabel}</span>
        </button>
      )}
    </div>
  );
};

export default BackButton;
